"use client";

import { usePathname } from "next/navigation";
import { useGlobalComponentData } from "@/lib/storyblok/hooks/useGlobalComponentData";
import type { Page } from "@/generated/sb";
import React from "react";
import {
  storyblokEditable,
  StoryblokServerComponent,
  StoryblokStory,
} from "@storyblok/react/rsc";
import { Main } from "@repo/ui-parkmobile/Main";
import { sectionResolver } from "@/lib/storyblok/adaptors";
import { groupSections } from "@/lib/groupSections";
import { extractPageTitle, extractPageDescription, extractArticles } from "@/lib/newsroom";
import { WebPageSchema, generateArticleListEntitySchema } from "./schema";

export interface PageProps {
  blok: Page;
}

export function Page({ blok }: PageProps) {
  const pathname = usePathname();
  const globalHeader = useGlobalComponentData(blok.header as string);
  const globalFooter = useGlobalComponentData(blok.footer as string);

  const { sections, enablePageSchema } = blok;

  if (!sections) return <div>No sections</div>;

  const baseUrl = process.env.NEXT_PUBLIC_DOMAIN || "";
  const pageUrl = `${baseUrl}${pathname}`;

  const pageTitle = enablePageSchema ? extractPageTitle(sections) : null;
  const pageDescription = enablePageSchema ? extractPageDescription(sections) : null;
  const articles = enablePageSchema ? extractArticles(sections, baseUrl) : [];
  const articleListEntity = articles.length > 0 ? generateArticleListEntitySchema({ items: articles }) : undefined;

  const groupedSections = groupSections(sections);
  return (
    <div className={`uipm:flex uipm:flex-col uipm:min-h-full`}>
      {enablePageSchema && pageTitle && (
        <WebPageSchema
          name={pageTitle}
          pageUrl={pageUrl}
          description={pageDescription ?? undefined}
          mainEntity={articleListEntity}
        />
      )}
      {globalHeader && (
        <StoryblokStory
          story={globalHeader}
          bridgeOptions={{
            resolveLinks: "url",
          }}
        />
      )}
      <Main {...storyblokEditable(blok)}>
        {groupedSections.map((group, groupIndex) =>
          group.sections.map((section, sectionIndex) => (
            <StoryblokServerComponent
              key={section._uid}
              blok={section}
              isHeroSection={groupIndex === 0 && sectionIndex === 0}
              {...storyblokEditable(section)}
              {...sectionResolver(section)}
            />
          )),
        )}
      </Main>
      {globalFooter && (
        <StoryblokStory
          story={globalFooter}
          bridgeOptions={{
            resolveLinks: "url",
          }}
        />
      )}
    </div>
  );
}
